<template>
  <div class="ps-table-wrap">
    <div class="table_header">
      <div class="group_by_select">
        <label class="group_by_label">{{
          $t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.CALL_LOGS.GROUP_BY')
        }}</label>
        <woot-button
          ref="dropdownTrigger"
          class-names="group-by-dropdown-trigger"
          icon="chevron-down"
          @click="toggleDropdown"
        >
          {{ selectedOptionLabel }}
        </woot-button>
        <div
          v-if="showDropdown"
          v-on-clickaway="closeDropdown"
          class="dropdown-pane dropdown-pane--open"
        >
          <woot-dropdown-menu>
            <woot-dropdown-item
              v-for="option in groupByOptions"
              :key="option.value"
            >
              <woot-button
                variant="clear"
                color-scheme="secondary"
                size="small"
                @click="onChangeGroupBy(option.value)"
              >
                {{ option.label }}
              </woot-button>
            </woot-dropdown-item>
          </woot-dropdown-menu>
        </div>
      </div>
    </div>
    <ve-table
      :fixed-header="true"
      :columns="currentColumns"
      :max-height="'calc(100vh - 210px)'"
      :sort-option="sortOption"
      :table-data="records"
      :border-around="true"
    />
    <div v-if="isLoading" class="loading_state">
      <spinner />
    </div>
    <empty-state
      v-else-if="!records.length"
      :title="$t('CAMPAIGN.OUTBOUND_CALL_CAMPAIGN.CALL_LOGS.EMPTY_STATE')"
    />
  </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';
import { VeTable } from 'vue-easytable';
import Spinner from 'shared/components/Spinner.vue';
import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';

export default {
  name: 'CallLogsTable',
  components: {
    VeTable,
    Spinner,
    WootDropdownItem,
    WootDropdownMenu,
    EmptyState,
  },
  mixins: [clickaway],
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    groupBy: {
      type: String,
      default: 'call',
    },
    records: {
      type: Array,
      default: () => [],
    },
    meta: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      showDropdown: false,
      sortConfig: {},
      sortOption: {
        sortAlways: true,
        sortIcons: {
          ascend: 'top-arrow',
          descend: 'bottom-arrow',
        },
        sortChange: params => {
          this.sortConfig = params;
          this.$emit('on-sort-change', params);
        },
      },
      groupByOptions: [
        { value: 'call', label: 'Call' },
        { value: 'contact', label: 'Contact' },
      ],
    };
  },
  computed: {
    selectedOptionLabel() {
      const option = this.groupByOptions.find(
        opt => opt.value === this.groupBy
      );
      return option ? option.label : 'Call';
    },
    currentColumns() {
      return this.groupBy === 'call' ? this.callColumns : this.contactColumns;
    },
    callColumns() {
      const matches = {};
      matches.date = {
        field: 'date',
        key: 'created_at',
        title: 'Date',
        width: 150,
        align: 'left',
        ...(this.isSortable('created_at') && {
          sortBy: this.getSortByKey('created_at'),
        }),
        renderBodyCell: ({ row }) => <span>{row.date}</span>,
      };
      matches.time = {
        field: 'time',
        key: 'time',
        title: 'Time',
        width: 120,
        align: 'left',
        renderBodyCell: ({ row }) => <span>{row.time}</span>,
      };
      matches.reference_id = {
        field: 'reference_id',
        key: 'reference_id',
        title: 'Call ID',
        width: 200,
        align: 'left',
        ...(this.isSortable('reference_id') && {
          sortBy: this.getSortByKey('reference_id'),
        }),
        renderBodyCell: ({ row }) => (
          <span class="call-id-cell" onClick={() => this.onClickRow(row.id)}>
            {row.reference_id}
          </span>
        ),
      };
      matches.contact = {
        field: 'contact',
        key: 'contact',
        title: 'Contact',
        width: 150,
        align: 'left',
        ...(this.isSortable('contact') && {
          sortBy: this.getSortByKey('contact'),
        }),
        renderBodyCell: ({ row }) => (
          <span>
            {row.caller_detail?.name || row.recipient_detail?.name || '-'}
          </span>
        ),
      };
      matches.duration = {
        field: 'duration',
        key: 'duration',
        title: 'Duration (minutes)',
        width: 150,
        align: 'left',
        ...(this.isSortable('duration') && {
          sortBy: this.getSortByKey('duration'),
        }),
        renderBodyCell: ({ row }) => <span>{row.duration}</span>,
      };
      matches.call_direction = {
        field: 'call_direction',
        key: 'call_direction',
        title: 'Direction',
        width: 150,
        align: 'center',
        ...(this.isSortable('call_direction') && {
          sortBy: this.getSortByKey('call_direction'),
        }),
        renderBodyCell: ({ row }) => (
          <span
            style={{
              display: 'inline-block',
              padding: '4px 12px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 500,
              backgroundColor:
                row.call_direction === 'outgoing'
                  ? 'var(--w-50)'
                  : 'var(--g-50)',
              color:
                row.call_direction === 'outgoing'
                  ? 'var(--w-600)'
                  : 'var(--g-600)',
            }}
          >
            {this.formatDirection(row.call_direction)}
          </span>
        ),
      };
      matches.kind = {
        field: 'kind',
        key: 'kind',
        title: 'Type',
        width: 150,
        align: 'left',
        renderBodyCell: ({ row }) => <span>{this.formatKind(row.kind)}</span>,
      };
      matches.result = {
        field: 'result',
        key: 'results',
        title: 'Outcome',
        width: 150,
        align: 'left',
        ...(this.isSortable('result') && {
          sortBy: this.getSortByKey('result'),
        }),
        renderBodyCell: ({ row }) =>
          row.results.map((result, index) => (
            <span
              key={index}
              style={{
                display: 'inline-block',
                padding: '2px 6px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 500,
                backgroundColor: this.getOutcomeColor(result.toLowerCase()).bg,
                color: this.getOutcomeColor(result.toLowerCase()).text,
                marginRight: '4px',
                marginBottom: '4px',
              }}
            >
              {this.formatResult(result)}
            </span>
          )),
      };

      return Object.values(matches);
    },
    contactColumns() {
      const matches = {};
      matches.contact = {
        field: 'contact',
        key: 'contact',
        title: 'Contact',
        width: 180,
        align: 'left',
        ...(this.isSortable('contact') && {
          sortBy: this.getSortByKey('contact'),
        }),
        renderBodyCell: ({ row }) => <span>{row.contact?.name || '-'}</span>,
      };
      matches.last_call_date = {
        field: 'last_call_date',
        key: 'last_call_date',
        title: 'Last Call Date',
        width: 150,
        align: 'left',
        ...(this.isSortable('last_call_date') && {
          sortBy: this.getSortByKey('last_call_date'),
        }),
        renderBodyCell: ({ row }) => <span>{row.last_call_date}</span>,
      };
      matches.last_call_time = {
        field: 'last_call_time',
        key: 'last_call_time',
        title: 'Last Call Time',
        width: 140,
        align: 'left',
        ...(this.isSortable('last_call_time') && {
          sortBy: this.getSortByKey('last_call_time'),
        }),
        renderBodyCell: ({ row }) => <span>{row.last_call_time}</span>,
      };
      matches.total_calls = {
        field: 'total_calls',
        key: 'total_calls',
        title: 'Total Call Attempts',
        width: 180,
        align: 'right',
        ...(this.isSortable('total_calls') && {
          sortBy: this.getSortByKey('total_calls'),
        }),
        renderBodyCell: ({ row }) => <span>{row.total_calls}</span>,
      };
      matches.total_duration = {
        field: 'total_duration',
        key: 'total_duration',
        title: 'Total Duration (seconds)',
        width: 150,
        align: 'left',
        ...(this.isSortable('total_duration') && {
          sortBy: this.getSortByKey('total_duration'),
        }),
        renderBodyCell: ({ row }) => (
          <span>{Math.round((row.total_duration || 0) * 60)}</span>
        ),
      };
      matches.result = {
        field: 'result',
        key: 'result',
        title: 'Outcome',
        width: 150,
        align: 'left',
        ...(this.isSortable('result') && {
          sortBy: this.getSortByKey('result'),
        }),
        renderBodyCell: ({ row }) => (
          <span
            style={{
              display: 'inline-block',
              padding: '4px 12px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 500,
              backgroundColor: this.getOutcomeColor(row.result).bg,
              color: this.getOutcomeColor(row.result).text,
            }}
          >
            {this.formatResult(row.result)}
          </span>
        ),
      };

      return Object.values(matches);
    },
  },
  methods: {
    getSortByKey(key) {
      const value = this.sortConfig[key];
      return value ? String(value) : '';
    },
    onClickRow(rowId) {
      this.$emit('on-row-click', rowId);
    },
    formatDirection(direction) {
      if (!direction) return '-';
      return direction === 'outgoing' ? 'Outgoing' : 'Incoming';
    },
    formatKind(kind) {
      if (!kind) return '-';
      return kind.charAt(0).toUpperCase() + kind.slice(1);
    },
    formatResult(result) {
      if (!result) return '-';
      return result.charAt(0).toUpperCase() + result.slice(1);
    },
    getOutcomeColor(outcomeClass) {
      const goodResults = ['success', 'positive', 'good', 'happy'];
      const badResults = ['bad', 'negative', 'dq', 'reject', 'angry'];

      if (goodResults.includes(outcomeClass)) {
        return { bg: '#4caf50', text: '#fff' };
      }
      if (badResults.includes(outcomeClass)) {
        return { bg: '#f87171', text: '#fff' };
      }
      return { bg: '#4a5568', text: '#fff' };
    },
    toggleDropdown() {
      this.showDropdown = !this.showDropdown;
    },
    closeDropdown() {
      this.showDropdown = false;
    },
    onChangeGroupBy(value) {
      this.showDropdown = false;
      this.$emit('change-group-by', value);
    },
    isSortable(key) {
      return Array.isArray(this.meta?.sorts) && this.meta.sorts?.includes(key);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~dashboard/assets/scss/widgets/_ps_table.scss';
.call_logs_table_container {
  width: 100%;
}

.call-id-cell {
  cursor: pointer;
  color: var(--w-500);

  &:hover {
    text-decoration: underline;
  }
}

.table_header {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 16px;
}

.group_by_select {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
}

.group_by_label {
  font-size: 14px;
  font-weight: 500;
  color: var(--s-700);
}

.group-by-dropdown-trigger {
  min-width: 120px;
}

.dropdown-pane {
  position: absolute;
  right: 0;
  top: 3rem;
  min-width: 140px;
  z-index: var(--z-index-much-higher);
}

.loading_state,
.empty_state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  color: var(--s-600);
  font-size: 14px;
}
</style>
